import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";

// Layout Components
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import MobileBottomBar from "@/components/layout/MobileBottomBar";
import ChatBubble from "@/components/layout/ChatBubble";

// Pages
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Team from "@/pages/Team";
import BeforeAfter from "@/pages/BeforeAfter";
import Contact from "@/pages/Contact";
import Referrals from "@/pages/Referrals";
import Book from "@/pages/Book";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import DataProtection from "@/pages/DataProtection";
import Complaints from "@/pages/Complaints";
import CookiePolicy from "@/pages/CookiePolicy";

function scrollToTopOnNavigate() {
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: prefersReducedMotion ? "auto" : "smooth",
  });
}

function PageTransition({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function RoutedPage({ children, withHeaderOffset = true }) {
  return (
    <PageTransition>
      <div className={withHeaderOffset ? "pt-20" : ""}>
        {children}
      </div>
    </PageTransition>
  );
}

function AppLayout() {
  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const isDirectClinicianJump = location.pathname === "/team" && Boolean(query.get("clinician"));
    if (isDirectClinicianJump) return;
    scrollToTopOnNavigate();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const pageTitleMap = {
      "/": "Specialist Dental London",
      "/services": "Services | Specialist Dental London",
      "/team": "Our Team | Specialist Dental London",
      "/before-after": "Results | Specialist Dental London",
      "/contact": "Contact | Specialist Dental London",
      "/book": "Book | Specialist Dental London",
      "/referrals": "Referrals | Specialist Dental London",
      "/privacy": "Privacy Policy | Specialist Dental London",
      "/data-protection": "Data Protection Policy | Specialist Dental London",
      "/complaints-policy": "Complaints Policy | Specialist Dental London",
      "/terms": "Terms & Conditions | Specialist Dental London",
      "/cookie-policy": "Cookie Policy | Specialist Dental London",
    };

    document.title = pageTitleMap[location.pathname] || "Specialist Dental London";
  }, [location.pathname]);

  // The old patient-portal routes are gone: there is no portal backend, so any
  // such URL falls through to the catch-all below and lands on the homepage
  // rather than on a page whose forms could not work.
  return (
    <>
      <Header />
      <main className="flex-1">
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<RoutedPage withHeaderOffset={false}><Home /></RoutedPage>} />
            <Route path="/services" element={<RoutedPage><Services /></RoutedPage>} />
            <Route path="/team" element={<RoutedPage><Team /></RoutedPage>} />
            <Route path="/before-after" element={<RoutedPage><BeforeAfter /></RoutedPage>} />
            <Route path="/contact" element={<RoutedPage><Contact /></RoutedPage>} />
            <Route path="/book" element={<RoutedPage><Book /></RoutedPage>} />
            <Route path="/referrals" element={<RoutedPage><Referrals /></RoutedPage>} />
            <Route path="/privacy" element={<RoutedPage><Privacy /></RoutedPage>} />
            <Route path="/data-protection" element={<RoutedPage><DataProtection /></RoutedPage>} />
            <Route path="/complaints-policy" element={<RoutedPage><Complaints /></RoutedPage>} />
            <Route path="/terms" element={<RoutedPage><Terms /></RoutedPage>} />
            <Route path="/cookie-policy" element={<RoutedPage><CookiePolicy /></RoutedPage>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
      <MobileBottomBar />
      <ChatBubble />
      <Toaster position="top-right" />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-warm-white">
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </div>
  );
}

export default App;
